/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Tab, Tabs} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Add} from '@material-ui/icons';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import {signal} from '@preact/signals-core';
import {useSignals} from '@preact/signals-react/runtime';
import classnames from 'classnames';
import isEmpty from 'lodash.isempty';
import {showJobCreateEditor} from '@ion/applications/src/components/jobs/state';
import {isPreferenceAvailable} from '@ion/applications/src/helpers';
import useCurrentGlobalGroupId from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import useFilePermissionsAction from '@ion/applications/src/hooks/useFilePermissionsAction';
import useFilePermissionsFilter from '@ion/applications/src/hooks/useFilePermissionsFilter';
import useGridColumnPersistence from '@ion/applications/src/hooks/useGridColumnPersistence';
import useJobSearchRerun from '@ion/applications/src/hooks/useJobSearchRerun';
import useViewSubGroups from '@ion/applications/src/hooks/useViewSubGroups';
import useAuthenticatedUser from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {clearDeviceSelection} from '@ion/applications/src/pages/devices/state';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {ExportContext} from '@ion/applications/src/types/redux';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {loadJobSearchEngine} from '@ion/core/src/services/Connect';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {DownloadFileTypes, Entity} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import ColumnChooserButton from '@ion/dls/src/components/buttons/ColumnChooserButton';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import MoreActionButton from '@ion/dls/src/components/buttons/MoreActionButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import RefreshGridButton from '@ion/dls/src/components/buttons/RefreshGridButton';
import {
  SaveIconButton,
  SaveIconTypes
} from '@ion/dls/src/components/buttons/SaveIconButton';
import Divider from '@ion/dls/src/components/divider/Divider';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import ResponsiveMenu from '@ion/dls/src/components/menus/ResponsiveMenu';
import useExpandCollapse from '@ion/dls/src/components/navigation/useExpandCollapse';
import SnackbarMessage from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import ViewSubGroupsSwitch from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import IonTooltip from '@ion/dls/src/components/tooltip/IonTooltip';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import EmailExportRequestDialog from '@ion/dls/src/export/EmailExportRequestDialog';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import useHasGridChanged from '@ion/dls/src/grid/useHasGridChanged';
import useScreenResize from '@ion/dls/src/hooks/useScreenResize';
import CancelIcon from '@ion/dls/src/icons/CancelIcon';
import {FacetsIcon} from '@ion/dls/src/icons/FacetsIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import {SideNavSensorsIcon} from '@ion/dls/src/icons/SideNavSensorsIcon';
import {UrgentIcon} from '@ion/dls/src/icons/UrgentIcon';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';
import Render from '@ion/dls/src/render/Render';
import {
  dlsAlt,
  dlsColor,
  dlsLayout
} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize, ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import QueryKey from '@ion/query/src/config/QueryKeys';
import useCancelJob from '@ion/query/src/hooks/useCancelJob';
import usePaginatedQueryCache from '@ion/query/src/hooks/usePaginatedQueryCache';
import useHasRolePermission from '@/security/useHasRolePermission';
import {
  addTab,
  displayRerunJobSearchEngineDialog,
  initializeJobs,
  NAME,
  removeTab,
  resetInitialTabs,
  resetNameFilter,
  setActiveTab,
  setAutoJobs,
  setJobs,
  setJobSearchEngineViewSubGroups,
  showAddDevices,
  UPDATE_GRID_COLUMNS
} from '@/store/actions/jobsearchengine';
import {PREFERENCE} from '@/store/reducers/jobsearchengine';
import {
  jobCancelSelectedSelector,
  jobCancelShowDialogSelector,
  jobSearchEngineActiveTabSelector,
  jobSearchEngineAutoJobsSelector,
  jobSearchEngineGridColumnStateSelector,
  jobSearchEngineTabsSelector
} from '@/store/selectors/jobsearchengine';
import {cancelJobs} from '../../../store/actions/jobsearchengine';
import FilePermissionsDialog from '../FilePermissionsDialog';
import DownloadJobCancelPrompt from '../jobs/DownloadJobCancelPrompt';
import DownloadJobRerunPrompt from '../jobs/DownloadJobRerunPrompt';
import DownloadJobsAudit from '../jobs/DownloadJobsAudit';
import {FilePermissions} from '../ProvisioningContainer';
import {
  liveGridActive,
  liveJobsEnabled,
  liveUpdatesPreferred,
  setLiveUpdatesPreferred,
  socketConnected
} from './signals/liveUpdates';
import {PermanentTabs, selectedJobFacetsSignal} from './signals/state';
import JobFacetsDrawer from './JobFacetsDrawer';
import {aggregationsCategoryMap, dateFacets} from './JobFacetUtils';
import JobSearchEngineGrid from './JobSearchEngineGrid';
import {isJobCancelDisabled, isRerunFailedAllowed} from './JobSearchEngineRowEditor';
import {
  buildWatchedJobsQuery,
  getTasksDevicesCounts,
  handleDisable,
  isJobTabLimitReached,
  isTerminalJobStatus
} from './JobSearchEngineUtils';
import {getDeleteMessage, getFilterByField} from './JobSearchUtils';
import JobSearchWidget from './JobSearchWidget';
import JobTab from './JobTab';
import useJobDeepLink from './useJobDeepLink';
import useJobGridLiveUpdates from './useJobGridLiveUpdates';
import {useJobPagination} from './useJobPagination';
import useJobSearchEngineExport from './useJobSearchEngineExport';
import useJobSearchEngineRerun from './useJobSearchEngineRerun';
import useJobSelectedFacets from './useJobSelectedFacets';
import {useWatchedJobIds} from './useWatchedJob';
import useWatchedJobDrops from './useWatchedJobDrops';
import {unwatchAll} from './WatchedJobs';

export interface JobSearchEngineProps {
  Features?: ApplicationFeatures;
  refreshInitialization?: Function;
  enableSubgroupToggle?: boolean;
  enableEmailExport?: boolean;
  enableDateFilter?: boolean;
}

export interface TabConfig {
  label: string;
  content: JSX.Element;
}

type JobStyles = {
  isFacetOpen: boolean;
  collapsed: boolean;
  gridHeight: number;
};

export const DRAWER_WIDTH          = 275;
export const GRID_MARGIN_EXPANDED  = 230;
export const GRID_MARGIN_LEFT      = 245;
export const GRID_MARGIN_COLLAPSED = 50;
export const RESOLUTION            = 1920;
export const ZOOM_RATIO            = 0.8;
export const ZOOM_RATIO_1          = 1;

const isStatusIndependentFacet = (category: string): boolean => (
  category.startsWith('createdOn') ||
  category === 'username-terms' ||
  category === 'tasks.metadata.platformId-terms'
);

const statusDependentSorts = ['progressStatus', 'updatedOn'];

export const useStyles = makeStyles((theme: DLSTheme) => {
  const transition      = 'all 200ms cubic-bezier(0.4, 0.0, 0.2, 1)';
  return {
    pageContainer : {
      display                        : 'flex',
      flexDirection                  : 'column',
      [theme.breakpoints.down('sm')] : {
        margin : '23px 23px 40px 23px'
      }
    },
    grid : {
      overflow   : 'hidden',
      position   : 'relative',
      transition : transition,
      marginLeft : ({isFacetOpen, collapsed}: JobStyles) => (
        isFacetOpen ? collapsed ? GRID_MARGIN_COLLAPSED : GRID_MARGIN_EXPANDED : 0
      )
    },
    gridFlex : {
      flex          : 1,
      display       : 'flex',
      flexDirection : 'column',
      position      : 'relative'
    },
    badge : {
      ...dlsAlt('typography', 'label3'),
      fontWeight      : 500,
      lineHeight      : '16px',
      fontSize        : '11px',
      letterSpacing   : '1px',
      backgroundColor : dlsColor('electricPurple'),
      color           : dlsColor('white'),
      padding         : '4px 10px 4px 11px',
      borderRadius    : '4px',
      marginRight     : '7px'
    },
    headerWrapper : {
      marginTop      : '50px',
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'space-between',
      alignItems     : 'center'
    },
    header : {
      marginTop     : '4px',
      flexShrink    : 0,
      color         : dlsColor('grey1'),
      fontSize      : '40px',
      fontWeight    : 400,
      letterSpacing : '0.5px',
      textTransform : 'capitalize'
    },
    addButton : {
      maxHeight : '38px'
    },
    hideAddButton : {
      display : 'none'
    },
    customTab : {
      color                  : dlsColor('grey1'),
      letterSpacing          : '1px',
      '& .MuiTabs-indicator' : {
        backgroundColor : dlsColor('grey1'),
        height          : '4px'
      },
      '& .MuiTab-root' : {
        ...dlsAlt('typography', 'label1'),
        color        : dlsColor('grey1'),
        fontWeight   : 600,
        minWidth     : 'auto',
        padding      : '0px 10px',
        marginBottom : '-4px',
        transition   : 'color 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        '&:hover'    : {
          color : dlsColor('black')
        }
      },
      '& .MuiTab-wrapper' : {
        marginBottom : '-10px'
      }
    },
    headerContainer : {
      display       : 'flex',
      flexDirection : 'row',
      alignItems    : 'center',
      gap           : '10px'
    },
    controls : {
      display        : 'flex',
      width          : '100%',
      justifyContent : 'space-between',
      alignItems     : 'center'
    },
    gridControls : {
      display      : 'flex',
      marginBottom : '10px'
    },
    tabContainer : {
      display       : 'flex',
      flexDirection : 'row',
      alignItems    : 'center',
      gap           : '12px'
    },
    tab : {
      textAlign      : 'center',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    customDivider : {
      backgroundColor : dlsColor('grey4'),
      marginBottom    : 10,
      marginTop       : -1,
      height          : 1
    },
    facetTrigger : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      width          : '32px',
      height         : '32px',
      marginBottom   : '-8px',
      borderRadius   : '4px',
      cursor         : 'pointer',
      transition     : transition,
      '&:hover'      : {
        '& svg' : {
          '& path' : {
            transition : transition,
            stroke     : dlsColor('aaBlue')
          }
        }
      }
    },
    facetCount : {
      ...dlsAlt('typography', 'cta2'),
      color           : dlsColor('white'),
      backgroundColor : dlsColor('caBlue'),
      padding         : '4px 8px',
      borderRadius    : '4px',
      marginTop       : '8px',
      cursor          : 'default'
    },
    clearAll : {
      ...dlsAlt('typography', 'cta2'),
      color     : dlsColor('aaBlue'),
      fontSize  : '12px',
      marginTop : '8px',
      cursor    : 'pointer',
      padding   : '4px 8px',
      '&:hover' : {
        textDecoration : 'underline'
      }
    },
    gridActions : {
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'space-between',
      alignItems     : 'center'
    },
    actions : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    subGroupsSwitch : {
      marginLeft : 12
    },
    copyLink : {
      border     : 'none',
      color      : dlsColor('aaBlue'),
      cursor     : 'pointer',
      alignItems : 'center',
      display    : 'inline-flex'
    },
    tabIcon : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      marginLeft     : 5,
      height         : '100%',
      cursor         : 'pointer',
      transition     : transition,
      '&:hover'      : {
        '& svg' : {
          transition : transition,
          '& path'   : {
            fill : dlsColor('grey2')
          }
        }
      }
    },
    menuPaper : {
      marginTop : '35px'
    }
  };
});

const paging = new SmartSearchPaging();

const liveUpdatesCaption = (): string => {
  let caption = translate('jobsearchengine:liveUpdates.disconnected');

  if (socketConnected.value) {
    caption = liveUpdatesPreferred.value
      ? translate('jobsearchengine:liveUpdates.on')
      : translate('jobsearchengine:liveUpdates.off');
  }

  return caption;
};

const JobSearchEngine = ({
  Features,
  refreshInitialization,
  enableEmailExport = true,
  enableDateFilter = false,
  enableSubgroupToggle = false
}: JobSearchEngineProps) => {
  useSignals();
  const {MY_JOBS, JOBS, WATCHING}                 = PermanentTabs;
  const isMounted                                 = useHasMounted();
  const {collapsed}                               = useExpandCollapse();
  const accountId                                 = useCurrentGlobalGroupId();
  const accountIdRef                              = useRef();
  const dispatch                                  = useDispatch();
  const isFacetOpen                               = signal<boolean>(UserPreferences.get('jobssearch.facets.show') ?? true);
  const setIsFacetOpen                            = createMutator<Object>(isFacetOpen);
  const [dropdownOpen, setDropdownOpen]           = useState(false);
  const [showDialog, setShowDialog]               = useState<boolean>(false);
  const [hoveredTab, setHoveredTab]               = useState<string | null>(null);
  const [platformId, setPlatformId]               = useState('');
  const [isFileTypeChecked, setIsFileTypeChecked] = useState(false);
  const [dateRange, setDateRange]                 = useState<string | null>(null);
  const tabs                                      = useSelector(jobSearchEngineTabsSelector);
  const activeTab                                 = useSelector(jobSearchEngineActiveTabSelector);
  const columnState                               = useSelector(jobSearchEngineGridColumnStateSelector);
  const [nameFilter, setNameFilter]               = useState(getFilterByField(tabs, activeTab, 'nameFilter'));
  const [jobFilters, setJobsFilters]              = useState(getFilterByField(tabs, activeTab, 'filters'));
  const [fileIds, setFileIds]                     = useState(getFilterByField(tabs, activeTab, 'fileIds'));
  const autoJobs                                  = useSelector(jobSearchEngineAutoJobsSelector);
  const [isOnlyAutoJobs, setIsOnlyAutoJobs]       = useState(autoJobs);
  const [isRerunFailed, setIsRerunFailed]         = useState(false);
  const [actionMenuOpen, setActionMenuOpen]       = useState(false);
  const ionGridRef                                = useRef<IonGridAPI>(null);
  const jobTabRef                                 = useRef(null);
  const sortOnRef                                 = useRef<{propertyName?: string}[] | null>(null);
  const tabsRef                                   = useRef(null);
  const actionMenuRef                             = useRef(null);
  const classes                                   = useStyles(
    {isFacetOpen: isFacetOpen.value, collapsed, gridHeight: 750});
  const userHasPermissionToAdd                    = !!(Features?.calampFileAllowed || Features?.customerFileAllowed);
  const filePermissions                           = useFilePermissionsFilter(Features, Operation.Assign);
  const hasFilePermissions                        = useFilePermissionsAction({Features});
  const isTabMaxLimitReached                      = isJobTabLimitReached(tabs);
  const isJobsListTab                             = Object.values(PermanentTabs).includes(activeTab);
  const {
    jobIds: watchedJobIds,
    revision: watchedJobsRevision
  }                                               = useWatchedJobIds();
  const [unwatchAllShow, setUnwatchAllShow]       = useState(false);
  const path                                      = translate('common:editors.filePermissions.info', {entity: Entity.job});
  const {
    getSelectedFacetsForTab,
    clearJobFacetsForTab
  } = useJobSelectedFacets();

  const {
    clearPaginationForTab,
    removePaginationForTab
  } = useJobPagination();

  // [ION CTC ADMIN PACKAGE TODO]: ADM Feature Management - Explicit Permissions Check
  // Anti-Pattern. The explicit Permissions Checks are to be refactored to reference 
  // Features exclusively when https://calampcorp.atlassian.net/browse/ADM-2245 is
  // implemented.
  const deviceFilesPermissions = {
    hasAddPermission      : useHasRolePermission(Entity.filemeta, Operation.Create) || false,
    hasReadPermission     : useHasRolePermission(Entity.filemeta, Operation.Read) || false,
    hasUpdatePermission   : useHasRolePermission(Entity.filemeta, Operation.Update) || false,
    hasDeletePermission   : useHasRolePermission(Entity.filemeta, Operation.Delete) || false,
    hasCustomerFileUpdate : useHasRolePermission(Entity.customerfile, Operation.Update) || false,
    hasCustomerFileRead   : useHasRolePermission(Entity.customerfile, Operation.Read) || false
  };

  // [ION CTC ADMIN PACKAGE TODO]: ADM Feature Management - Explicit Permissions Check
  // Anti-Pattern. The explicit Permissions Checks are to be refactored to reference 
  // Features exclusively when https://calampcorp.atlassian.net/browse/ADM-2245 is
  // implemented.
  const jobsPermissions = {
    hasAddPermission    : useHasRolePermission(Entity.job, Operation.Create) || false,
    hasReadPermission   : useHasRolePermission(Entity.job, Operation.Read) || false,
    hasUpdatePermission : useHasRolePermission(Entity.job, Operation.Update) || false,
    hasDeletePermission : useHasRolePermission(Entity.job, Operation.Delete) || false
  };

  /**
   * Effect which determines if the user has pending preferences,
   * and if so, persists the users preferences to external services.
   */
  useEffect(() => () => {
    if (UserPreferences.hasChanges()) {
      UserPreferences.save();
    }
  }, []);

  const facetCount = useMemo(() => {
    const facets = getSelectedFacetsForTab(activeTab);
    const selectedFacetCount = Object.values(facets)?.reduce((total, facetArray) => {
      return total + (Array.isArray(facetArray) ? facetArray.length : 0);
    }, 0);
    return selectedFacetCount;
  }, [selectedJobFacetsSignal.value, dateRange, activeTab]);

  const onAdd = useCallback(function() {
    if (!isEmpty(filePermissions)) {
      dispatch(clearDeviceSelection());
      dispatch(showJobCreateEditor([], null, true));
    } else {
      setShowDialog(true);
    }
  }, [filePermissions]);
  
  const closeDialog = useCallback(function() {
    setShowDialog(false);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeJobs());
      accountIdRef.current = accountId;
    }
  }, [isMounted]);

  useEffect(() => {
    tabsRef.current = tabs?.length ? tabs : [];
    setNameFilter(getFilterByField(tabs, activeTab, 'nameFilter'));
    setFileIds(getFilterByField(tabs, activeTab, 'fileIds'));
    setJobsFilters(getFilterByField(tabs, activeTab, 'filters'));
  }, [tabs, activeTab]);

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setJobSearchEngineViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : PREFERENCE
  });

  const [autoSaveGridOption, setAutoSaveGridOption] = useState(false);
  const [gridSaveInProgress, setGridSaveInProgress] = useState(false);
  const [containerHeight, setContainerHeight]       = useState(false);
  const [currentJob, setCurrentJob]                 = useState();
  const [taskId, setTaskId]                         = useState();
  const [enableApi, setEnableApi]                   = useState(false);
  const containerMargin                             = dlsLayout('header', 'desktop');
  const hasChangeInGrid                             = useHasGridChanged(NAME);
  const {DEFAULT, CHANGED, IN_PROGRESS}             = SaveIconTypes;
  const gridMode                                    = hasChangeInGrid ? CHANGED : DEFAULT;
  const gridState                                   = gridSaveInProgress ? IN_PROGRESS : gridMode;
  const user                                        = useAuthenticatedUser();
  const cancelSelected                              = useSelector(jobCancelSelectedSelector);
  const cancelShow                                  = useSelector(jobCancelShowDialogSelector);

  const {
    failureMessage,
    successMessage
  } = translate('jobsearchengine:cancel', {
    returnObjects : true,
    entity        : Entity.job
  }) || {};

  useEffect(() => {
    setAutoSaveGridOption(isPreferenceAvailable('persistGridLayouts'));
  }, [user]);

  const onUpdateGridColumns = useGridColumnPersistence({
    action : UPDATE_GRID_COLUMNS,
    name   : NAME,
    columnState
  });

  const buildFacetQueryString = useCallback((facets: Record<string, string[]>, existingQuery: string = '') => {
    let query = existingQuery;
    Object.keys(facets).forEach((facetCategory) => {
      if (facetCategory === 'createdOn' || facetCategory === 'tasks.metadata.platformId-terms') {
        return;
      }
      const selectedValues = facets[facetCategory];
      if (selectedValues?.length) {
        const cleanedCategory = facetCategory.replace('-terms', '');
        const facetQuery      = selectedValues.map((value) => `${cleanedCategory}:${cleanedCategory === 'username' ? `(${value})` : value}`).join(' OR ');

        query += ` AND (${facetQuery})`;
      }
    });
    return query;
  }, []);

  const queryString = useMemo(() => {
    let currentFacets = {};
    const currentFilter = activeTab === WATCHING
      ? buildWatchedJobsQuery(watchedJobIds)
      : tabs.find(tab => tab.key === activeTab)?.filter;

    if (Object.values(PermanentTabs).includes(activeTab)) {
      currentFacets = getSelectedFacetsForTab(activeTab);
    }

    return buildFacetQueryString(currentFacets, currentFilter);
  }, [selectedJobFacetsSignal.value, activeTab, tabs, watchedJobsRevision]);

  useEffect(() => {
    dispatch(showAddDevices());
    checkZoomLevel();
    return function() {
      dispatch(showAddDevices(false));
    };
  }, []);

  const [apiParams, setApiParams] = useState({});
  const apiParamsRef              = useRef({});

  useEffect(() => {
    const createdOnValue = getSelectedFacetsForTab(activeTab).createdOn?.[0];
    const platformValue  = getSelectedFacetsForTab(activeTab)?.['tasks.metadata.platformId-terms']?.[0];
    const isChecked      = platformValue?.includes(platformId);

    setPlatformId(platformValue);
    setIsFileTypeChecked(isChecked);

    if (createdOnValue) {
      setDateRange?.(dateFacets[createdOnValue].dates);
    }
  }, [activeTab, platformId]);

  // pageId/pageSize intentionally absent - getJobs seeds them itself, and
  // depending on jobPaginationState here would rebuild apiParams on every
  // page change, resetting ag-grid's datasource back to page 1...
  //
  // tabs is absent too (not read by getJobs/the API) - and paging (a
  // module-level constant) doesn't need to be a dep. Both would otherwise
  // rebuild apiParams on any unrelated tab open/close, resetting the page...
  //
  // jobFilters and selectedJobFacetsSignal.value are absent for the same
  // reason: getJobs always overrides filters with its own jobFilters
  // closure value, and queryString already folds facets into a stable
  // string, so both are dead weight here that only add instability...
  useEffect(() => {
    const params = {
      sortOn           : [{propertyName: 'jobId', order: 'DESC'}],
      includeSubGroups : viewSubGroups,
      query            : queryString,
      nameFilter,
      fileIds,
      isFileTypeChecked,
      platformId,
      dateRange,
      accountId,
      paging,
      activeTab
    };
    apiParamsRef.current = params;
    setApiParams(params);
  }, [
    isFileTypeChecked,
    viewSubGroups,
    queryString,
    activeTab,
    platformId,
    dateRange,
    nameFilter,
    fileIds
  ]);

  const resolveResults = ({items = [], aggregations = [], totalCount = 0}) => {
    return {items, aggregations, totalCount};
  };

  const {
    data,
    loadItems,
    refresh,
    onRefresh,
    paging: tabPaging,
    createQuerykey,
    queryClient,
    fetching: isFetching
  } = usePaginatedQueryCache({
    baseKey         : QueryKey.JOB_SEARCH_ENGINE,
    api             : loadJobSearchEngine,
    parser          : resolveResults,
    parseKey        : 'items',
    initialPage     : 1,
    initialPageSize : 20,
    // prefetch races paging.nextPage()'s state update against the ref
    // usePaginatedQueryCache only updates from loadItems, which can cache a
    // page's data under the wrong query key. Disabled until fixed centrally...
    enabledPrefetch : false,
    apiParams
  });

  const {
    isError: jobCancelError,
    isSuccess: jobCancelSuccess,
    isLoading: jobCancelLoading,
    cancelJob: cancelSelectedJob
  } = useCancelJob();

  const {
    rerunShow,
    rerunSelected,
    rerunFailedShow,
    rerunFailedTaskShow
  } = useJobSearchEngineRerun();

  const {
    reRunJobSelected,
    jobRerunLoading,
    reRunJobError,
    rerunFailed,
    rerunJob
  } = useJobSearchRerun();

  const {
    isEmailExportRequest,
    setIsEmailExportRequest,
    onCloseEmailExportDialog
  } = useJobSearchEngineExport();

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const membershipDependsOnStatus = useCallback((): boolean => {
    const facets = getSelectedFacetsForTab(activeTab);

    return Object.keys(facets).some(category => (
      !!facets[category]?.length && !isStatusIndependentFacet(category)
    )) || !!sortOnRef.current?.some(
      entry => statusDependentSorts.includes(entry?.propertyName ?? '')
    );
  }, [activeTab, getSelectedFacetsForTab]);

  const {observe, loadBlock, dropJobs, reconciling} = useJobGridLiveUpdates({
    queryClient,
    ionGridRef,
    onRefresh,
    membershipDependsOnStatus
  });

  const {suppressRefresh} = useWatchedJobDrops({
    isWatchingTab : activeTab === WATCHING,
    revision      : watchedJobsRevision,
    onDrop        : dropJobs,
    watchedJobIds
  });

  const isUserFetching = isFetching && !reconciling;

  const getJobs = useCallback(async({
    callback,
    sort,
    pageId,
    pageSize
  }) => {
    jobTabRef.current = sort;
    sortOnRef.current = sort;

    const params = {
      ...apiParamsRef?.current,
      pageId,
      filters : jobFilters,
      sortOn  : sort,
      viewSubGroups,
      nameFilter,
      fileIds,
      accountId,
      pageSize
    };
    const response = await loadBlock(
      createQuerykey(params), () => loadItems({...params, callback})
    );

    if (response) {
      observe((response.items ?? [])
        .filter(item => !isTerminalJobStatus(item.progressStatus))
        .map(item => item.jobId));
    }
    return response;
  }, [
    viewSubGroups,
    createQuerykey,
    loadBlock,
    loadItems,
    accountId,
    apiParams,
    nameFilter,
    observe,
    fileIds,
    jobFilters
  ]);

  const handleAutoJobsClick = useCallback(() => {
    setIsOnlyAutoJobs(prevState => {
      dispatch(setAutoJobs(!prevState));
      if (prevState) {
        dispatch(resetNameFilter());
      }
      return !prevState;
    });
  }, []);

  const clearAllFacets = useCallback(() => {
    setPlatformId(null);
    setIsFileTypeChecked(false);
    clearJobFacetsForTab(activeTab);
    setDateRange(null);
    clearPaginationForTab(activeTab);
    onRefresh();
  }, [activeTab, onRefresh]);

  const handleViewSubGroups = useCallback(function() {
    toggleViewSubGroups?.(!viewSubGroups);
    clearPaginationForTab(activeTab);
  }, [viewSubGroups, activeTab]);

  const visibleTabs = useMemo(() => (
    liveJobsEnabled.value ? tabs : tabs.filter(tab => tab.key !== WATCHING)
  ), [tabs, liveJobsEnabled.value]);

  const handleTabChange = useCallback((_, newValue) => {
    const currentTab =  visibleTabs[newValue].key;
    const createdOnValue = getSelectedFacetsForTab(currentTab).createdOn?.[0];
    setDateRange?.(null);
    if (createdOnValue) {
      const selectedDateFacet = dateFacets[createdOnValue];
      setDateRange?.(selectedDateFacet.dates);
    }
    setDropdownOpen(false);
    dispatch(setActiveTab(currentTab));
    setEnableApi(true);
  }, [visibleTabs, dispatch, activeTab]);

  // Function to handle tab close (remove tab)
  const handleTabClose = useCallback((tabKey) => {
    if (tabKey === activeTab) {
      dispatch(setActiveTab(MY_JOBS));
    }
    clearJobFacetsForTab(tabKey);
    removePaginationForTab(tabKey);
    dispatch(removeTab(tabKey));
  }, [dispatch, removePaginationForTab]);

  // Function to add new job tabs
  const handleAddTab = useCallback((jobId, status) => {
    const jobTabs     = tabsRef?.current;
    const jobStatus   = status ?? data?.current?.items?.find(job => job.jobId === jobId)?.progressStatus;
    const existingTab = jobTabs.find(tab => tab.key === `job-${jobId}`);
    if (!existingTab && !isJobTabLimitReached(jobTabs)) {
      const tab = {
        key    : `job-${jobId}`,
        label  : jobId,
        filter : `(jobId:${jobId})`,
        type   : 'job-view',
        icon   : true,
        failed : jobStatus === 'Failed'
      };
      dispatch(addTab(tab));
      dispatch(setActiveTab(`job-${jobId}`));
    } else {
      dispatch(setActiveTab(`job-${jobId}`));
    }
    setEnableApi(true);
  }, [tabs, dispatch]);

  const idValue = useJobDeepLink({handleAddTab});

  useEffect(() => {
    if (accountIdRef?.current !== accountId && !idValue) {
      dispatch(resetInitialTabs());
      setIsOnlyAutoJobs(false);
      dispatch(setActiveTab(MY_JOBS));
      setIsFileTypeChecked(false);
      setPlatformId(null);
      setDateRange(null);
    }
    accountIdRef.current = accountId;
    onRefresh();
  }, [accountId, idValue]);

  useEffect(() => {
    if (jobCancelSuccess || jobCancelError) {
      dispatch(cancelJobs(null, false));
    }
  }, [jobCancelSuccess, jobCancelError]);

  useEffect(() => {
    dispatch(setJobs(data?.current?.items));
  }, [data?.current?.items]);

  // Handle column chooser click
  const handleToggleClick = function() {
    if (ionGridRef.current && typeof ionGridRef.current.onToggleColumnChooser === 'function') {
      ionGridRef.current.onToggleColumnChooser();
    }
  };

  function handleCloseTab(tab, e) {
    e.preventDefault();
    e.stopPropagation();
    if (tab.key === activeTab) {
      const currentTabIndex = tabs.findIndex(t => t.key === tab.key);
      const nextTab         = currentTabIndex > 0 ? tabs[currentTabIndex - 1] : {key: MY_JOBS};
      dispatch(setActiveTab(nextTab.key));
    }
    handleTabClose(tab.key);
  }

  const onRefreshPage = (params) => {
    if (isJobsListTab) {
      onRefresh(params);
    } else {
      jobTabRef?.current?.refresh();
    }
  };

  const getExportColumnDefinitions = function() {
    return {
      columns          : ionGridRef?.current?.getExportParams() || {},
      individualColumn : translate('jobsearchengine:exportColumns', {returnObjects: true})
    };
  };

  const onExport = useCallback(function() {
    setIsEmailExportRequest?.(true);
  }, [setIsEmailExportRequest]);

  const updateGridSettings = function() {
    if (hasChangeInGrid && onUpdateGridColumns) {
      setGridSaveInProgress(true);
      onUpdateGridColumns();
      // adding delay help to show the transition of save icon
      // from changed to default.
      setTimeout(() => {
        setGridSaveInProgress(false);
        dispatch(toggleUserPrompt({
          message          : translate('common:grids.success'),
          autoHideDuration : 3000
        }));
      }, 1000);
    }
  };

  const hasJob = useCallback((): boolean => {
    return isJobsListTab;
  }, [activeTab]);

  const handleExportIcon = useCallback((): boolean => {
    if (hasJob()) {
      return data?.current?.totalCount === 0;
    }
  }, [activeTab, data]);

  const onJobDeleteConfirm = useCallback(function() {
    cancelSelectedJob(cancelSelected);
  }, [cancelSelected]);

  const onJobDeleteCancel = useCallback(function() {
    dispatch(cancelJobs(cancelSelected, false));
  }, [cancelSelected]);

  const getJobDeleteMessage = useCallback(function() {
    return getDeleteMessage(cancelSelected);
  }, [cancelSelected]);

  const onRerunConfirm = useCallback(function() {
    dispatch(displayRerunJobSearchEngineDialog(rerunSelected, false, false));
    if (rerunFailedShow || rerunFailedTaskShow) {
      rerunFailed(rerunSelected, taskId, rerunFailedTaskShow);
    } else {
      rerunJob(rerunSelected);
    }
  }, [taskId, rerunSelected, rerunFailedShow, rerunFailedTaskShow]);
  
  const [hasFilePermissionFeature, setHasFilePermissionFeature] = useState(false);
  const hasAssignFilePermission                                 = useFilePermissionsAction({
    Features,
    isEditor : true
  });
  
  useEffect(() => {
    const handleAccess = () => {
      if (currentJob) {
        if (!isEmpty(hasAssignFilePermission)) {
          setHasFilePermissionFeature(handleDisable(currentJob, Features, hasAssignFilePermission));
        }
      }
    };
    
    handleAccess();
  }, [
    hasAssignFilePermission,
    Features,
    currentJob
  ]);

  const rerunTaskJob = useCallback((taskId, reRunFailed = false, reReunTask = false) => {
    return () => {
      if (!hasFilePermissionFeature) {
        setTaskId(taskId);
        const selectedJob = {
          ...currentJob,
          details : getTasksDevicesCounts(currentJob?.tasks)
        };
        dispatch(displayRerunJobSearchEngineDialog(
          selectedJob,
          reRunFailed,
          reReunTask ? !reReunTask : !reRunFailed,
          reReunTask
        ));
      }
    };
  }, [currentJob, hasFilePermissionFeature]);
    
  const onCancel = useCallback(() => {
    setActionMenuOpen(false);
    dispatch(cancelJobs(currentJob));
  }, [currentJob]);
  
  const {
    hasDeletePermission,
    hasUpdatePermission
  } = jobsPermissions || {};
  
  const onRerunCancel = useCallback(function() {
    dispatch(displayRerunJobSearchEngineDialog(rerunSelected, false, false));
  }, [rerunSelected]);

  const checkZoomLevel = () => {
    const zoomRatio        = window.devicePixelRatio;
    const systemResolution = devicePixelRatio * window.innerWidth;
    
    setContainerHeight(zoomRatio > (systemResolution >= RESOLUTION ? ZOOM_RATIO_1 : ZOOM_RATIO));
  };

  useScreenResize(checkZoomLevel);

  const getContainerHeight = useMemo(() => {
    const heightObj = {height: `calc(100vh - ${containerMargin}px)`};

    if (isJobsListTab || !containerHeight) {
      return heightObj;
    }
    return {};
  }, [activeTab, containerHeight, containerMargin]);

  const rerunFailedAllowed = useCallback(() => {
    return isRerunFailedAllowed(currentJob, currentJob?.progressStatus, true);
  }, [currentJob]);

  const onDisable = useCallback(() => {
    if (hasDeletePermission) {
      return isJobCancelDisabled(currentJob?.progressStatus);
    }
    return true;
  }, [currentJob]);

  const onToggleActionMenu = useCallback(() => {
    setActionMenuOpen(!actionMenuOpen);
  }, [actionMenuOpen]);

  const onUnwatchAll = useCallback(function() {
    setActionMenuOpen(false);
    setUnwatchAllShow(true);
  }, []);

  const onUnwatchAllConfirm = useCallback(function() {
    setUnwatchAllShow(false);
    unwatchAll();
  }, []);

  const onUnwatchAllCancel = useCallback(function() {
    setUnwatchAllShow(false);
  }, []);

  return (
    <div className={classes.pageContainer}
      style={getContainerHeight}>
      <div className={classes.headerWrapper}>
        <div className={classes.headerContainer}>
          <div className={classes.header}>
            {translate('jobsearchengine:name')}
          </div>
          <Render.If condition={isJobsListTab}>
            <div className={classes.subGroupsSwitch}>
              <ViewSubGroupsSwitch checked={viewSubGroups}
                disabled={isUserFetching}
                onChange={handleViewSubGroups}
                preference={PREFERENCE}
                position={ViewSubGroupsPosition.GRID_CTAS}
                labelColor={dlsColor('grey2')} />
            </div>
            <div>
              <ViewSubGroupsSwitch label={translate('jobsearchengine:onlyAutoJobs')}
                disabled={isUserFetching}
                checked={isOnlyAutoJobs}
                onChange={handleAutoJobsClick}
                position={ViewSubGroupsPosition.GRID_CTAS}
                labelColor={dlsColor('grey2')} />
            </div>
          </Render.If>
        </div>
        <Tooltip arrow title={hasFilePermissions && path}
          disableHoverListener={!hasFilePermissions}>
          <div>
            <PrimaryButton className={classnames({
              [classes.hideAddButton] : !userHasPermissionToAdd &&
            !jobsPermissions?.hasAddPermission
            })}
            disabled={hasFilePermissions}
            startIcon={<Add />}
            onClick={onAdd}>
              {translate('common:addNew')}
            </PrimaryButton>
          </div>
        </Tooltip>
      </div>
      <div className={classes.gridActions}>

        <div className={classes.tabContainer}>
          <div className={classes.facetTrigger} onClick={
            function() {
              const newValue = !isFacetOpen.value;
              UserPreferences.set('jobssearch.facets.show', newValue);
              setIsFacetOpen(newValue);
            }}
          style={{
            transform : isFacetOpen.value ? 'rotate(90deg)' : 'rotate(0deg)'
          }}>
            <FacetsIcon color={data?.current?.aggregations && isFacetOpen.value ? dlsColor('aaBlue') : dlsColor('grey3')} />
          </div>
          <Render.If condition={facetCount > 0}>
            <div className={classes.facetCount}>
              {facetCount}
            </div>
            <div className={classes.clearAll} onClick={clearAllFacets}>
              {translate('jobsearchengine:facets.clearAll')}
            </div>
          </Render.If>
          <Tabs value={visibleTabs.findIndex(tab => tab.key === activeTab)}
            onChange={handleTabChange}
            className={classes.customTab}>
            {visibleTabs.map(tab => (
              <Tab key={tab.key}
                onMouseEnter={function() { if (!dropdownOpen) { setHoveredTab(tab.key); } }}
                onMouseLeave={function() { if (!dropdownOpen) { setHoveredTab(null); } }}
                label={
                  <div className={classes.tab}>
                    <span>{tab.label}</span>
                    <Render.If condition={tab?.key !== MY_JOBS && tab.key !== JOBS && tab?.icon}>
                      <div className={classes.tabIcon}
                        onClick={function(e) { handleCloseTab(tab, e); }}>
                        {hoveredTab === tab.key ? (
                          <MaterialExitCloseIcon size={16} />
                        ) : (
                          tab?.failed && <UrgentIcon />
                        )}
                      </div>
                    </Render.If>
                  </div>
                }/>
            ))}
          </Tabs>
        </div>
        <div className={classes.actions}>
          <Render.If condition={!isJobsListTab}>
            <IonTooltip caption={translate('jobsearchengine:grid.menu.rerunFailedJobTitle')}>
              <IconButton onClick={rerunTaskJob(0, true)}
                isDisableBackgroundColor={false}
                disabled={!rerunFailedAllowed() || hasFilePermissionFeature || !hasUpdatePermission}
                icon={<RetryIcon size={IconSize.medium}
                  opacity={0.5}
                  disable={!rerunFailedAllowed() || hasFilePermissionFeature || !hasUpdatePermission}/>} />
            </IonTooltip>
          </Render.If>
          <RefreshGridButton onRefresh={onRefreshPage as (params?: boolean) => void} />
          <Render.If condition={liveJobsEnabled.value}>
            <IonTooltip caption={liveUpdatesCaption()}>
              <div>
                <IconButton icon={<SideNavSensorsIcon />}
                  isSelected={liveGridActive.value}
                  disabled={!socketConnected.value}
                  onClick={function() {
                    setLiveUpdatesPreferred(!liveUpdatesPreferred.value);
                  }} />
              </div>
            </IonTooltip>
          </Render.If>
          <Render.If condition={(onUpdateGridColumns && !autoSaveGridOption && isJobsListTab)}>
            <SaveIconButton type={gridState} onClick={updateGridSettings} />
          </Render.If>
          <CSVExportButton onExport={onExport}
            disableExport={enableEmailExport}
            exportLimit={enableEmailExport}
            hasNoRowSelection={enableEmailExport}
            enableExport={handleExportIcon()} />
          <Render.If condition={isJobsListTab}>
            <ColumnChooserButton onShowColumnChooser={handleToggleClick} />
          </Render.If>
          <Render.If condition={!isJobsListTab}>
            <IonTooltip caption={translate('jobsearchengine:grid.menu.actions')}>
              <div ref={actionMenuRef}>
                <MoreActionButton hideTitle onAction={onToggleActionMenu} />
              </div>
            </IonTooltip>
            <ResponsiveMenu open={actionMenuOpen}
              onClose={onToggleActionMenu}
              anchorEl={actionMenuRef.current}
              menuClasses={{paper: classes.menuPaper}}>
              <CellMenuItem icon={<CancelIcon size={IconSize.medium}
                disable={hasDeletePermission ? onDisable() : true}
                opacity={0.5} />}
              onClick={onCancel}
              disabled={hasDeletePermission ? onDisable() : true}
              label={translate('jobsearchengine:grid.menu.cancelJob')} />
            </ResponsiveMenu>
          </Render.If>
          <Render.If condition={activeTab === WATCHING}>
            <IonTooltip caption={translate('jobsearchengine:grid.menu.actions')}>
              <div ref={actionMenuRef}>
                <MoreActionButton hideTitle onAction={onToggleActionMenu} />
              </div>
            </IonTooltip>
            <ResponsiveMenu open={actionMenuOpen}
              onClose={onToggleActionMenu}
              anchorEl={actionMenuRef.current}
              menuClasses={{paper: classes.menuPaper}}>
              <CellMenuItem icon={<NotificationsOffIcon fontSize='small'
                htmlColor={dlsColor('grey3')} />}
              onClick={onUnwatchAll}
              disabled={!watchedJobIds?.length}
              label={translate('jobsearchengine:notifications.unwatchAll')} />
            </ResponsiveMenu>
          </Render.If>
        </div>
      </div>
      <Divider className={classes.customDivider} />
      <Render.If condition={isJobsListTab}>
        <JobFacetsDrawer aggregations={data?.current?.aggregations}
          deviceTypes={Features?._deviceTypes}
          setIsFileTypeChecked={setIsFileTypeChecked}
          setPlatformId={setPlatformId}
          setDateRange={setDateRange}
          activeTab={activeTab}
          isFacetOpen={isFacetOpen.value}
          facetCategories={aggregationsCategoryMap?.[activeTab]}
          isLoading={isUserFetching}
          onRefresh={onRefresh} />
        <div className={classnames(classes.grid, classes.gridFlex)} style={{
          marginLeft : isFacetOpen.value ? GRID_MARGIN_LEFT : 0
        }}>
          <JobSearchWidget activeTab={activeTab}
            accountId={accountId}
            viewSubGroups={viewSubGroups}
            isOnlyAutoJobs={isOnlyAutoJobs} />
          <JobSearchEngineGrid loadItems={getJobs}
            setIsRerunFailed={setIsRerunFailed}
            isTabMaxLimitReached={isTabMaxLimitReached}
            handleJobClick={handleAddTab}
            viewSubGroups={viewSubGroups}
            toggleSubGroups={handleViewSubGroups}
            deviceTypes={Features?._deviceTypes}
            onRefresh={onRefresh}
            Features={Features}
            jobsPermissions={jobsPermissions}
            refreshInitialization={refreshInitialization}
            activeTab={activeTab}
            tabPaging={tabPaging}
            ref={ionGridRef}
            refresh={(refresh && !suppressRefresh) || jobCancelLoading || jobRerunLoading} />
        </div>
      </Render.If>
      <Render.If condition={!isJobsListTab}>
        <div className={classes.grid} style={{marginLeft: isFacetOpen.value ? GRID_MARGIN_LEFT : 0}}>
          <JobTab key={activeTab}
            ref={jobTabRef}
            setEnableApi={setEnableApi}
            enableApi={enableApi}
            containerHeight={containerHeight}
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            activeTab={activeTab}
            isFacetOpen={isFacetOpen.value}
            params={apiParams}
            Features={Features}
            currentJob={currentJob}
            setCurrentJob={setCurrentJob}
            setIsRerunFailed={setIsRerunFailed}
            rerunJob={rerunTaskJob}
            jobsPermissions={jobsPermissions}
            jobId={activeTab?.split('-')?.[1]} />
        </div>
      </Render.If>
      <Render.If condition={isEmpty(filePermissions)}>
        <FilePermissionsDialog show={showDialog}
          title={translate('common:filePermissions.title', {entity: FilePermissions.DownloadJob})}
          message={translate('common:filePermissions.message')}
          actionLabel={translate('common:filePermissions.actionLabel')}
          onAction={closeDialog} />
      </Render.If>
      <DownloadJobsAudit enableDateFilter={enableDateFilter} />
      <DownloadJobRerunPrompt show={rerunShow || rerunFailedShow || rerunFailedTaskShow}
        rerunFailedTaskShow={rerunFailedTaskShow}
        rerunFailedShow={rerunFailedShow}
        onRerunConfirm={onRerunConfirm}
        onRerunCancel={onRerunCancel}
        isRerunFailed={isRerunFailed}
        setIsRerunFailed={setIsRerunFailed}
        downloadjob={rerunSelected}
        result={reRunJobSelected}
        loading={jobRerunLoading}
        error={reRunJobError}
        taskId={taskId} />
      <DownloadJobCancelPrompt show={cancelShow}
        isCancelJob={true}
        onDeleteConfirm={onJobDeleteConfirm}
        onDeleteCancel={onJobDeleteCancel}
        overrideMessage={getJobDeleteMessage}
        downloadjob={cancelSelected}
        result={jobCancelSuccess && successMessage}
        loading={jobCancelLoading}
        error={jobCancelError && failureMessage} />
      <Render.If condition={isEmailExportRequest}>
        <EmailExportRequestDialog exportCount={data?.current?.totalCount}
          show={isEmailExportRequest}
          isIndividualJob={!hasJob()}
          isJobSearchEngine={enableEmailExport}
          exportContext={ExportContext.jobs}
          format={DownloadFileTypes.XLSX}
          isFileTypeChecked={isFileTypeChecked}
          platformId={platformId}
          dateRange={dateRange}
          filters={jobFilters}
          nameFilter={nameFilter}
          fileIds={fileIds}
          sortOn={jobTabRef?.current?.sortOn || jobTabRef?.current}
          searchFilters={jobTabRef?.current?.searchCriteria?.trim() || queryString?.trim()}
          viewSubGroups={viewSubGroups}
          additionalProperties={['description', 'account', 'progress', 'task.counts', 'task.progress']}
          getExportColumnDefinitions={getExportColumnDefinitions}
          accountId={accountId}
          showSnackbar={onComplete}
          onClose={onCloseEmailExportDialog} />
      </Render.If>
      <UserNotificationDialog show={unwatchAllShow}
        title={translate('jobsearchengine:notifications.unwatchAllTitle')}
        message={translate('jobsearchengine:notifications.unwatchAllMessage', {
          count : watchedJobIds?.length
        })}
        actionLabel={translate('common:editors.shared.yesContinue')}
        dismissLabel={translate('common:editors.shared.neverMind')}
        onAction={onUnwatchAllConfirm}
        onDismiss={onUnwatchAllCancel}
        onBackdropClick={onUnwatchAllCancel}
        capitalize />
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        onDismiss={dismissBulkSnackbar} />
    </div>
  );
};

export default JobSearchEngine;
